/**
 * Orchestration du rendu : d'un `EventRow` + un index de fond vers une image PNG.
 *
 * Attend explicitement polices, logo et éventuelle photo avant de dessiner
 * (sinon rendu raté et silencieux). Déterministe : mêmes données + même fond →
 * même image. Quand l'événement a une photo, elle sert de fond (index 0) ; sinon
 * un dégradé sombre. Une photo inaccessible retombe proprement sur un dégradé.
 */

import { toShareCardData } from '@/lib/share/eventCardData';
import { SHARE_GRADIENTS, categoryAccent, resolveBackground } from '@/lib/share/backgrounds';
import { ensureShareFontsLoaded, loadShareBackgroundImage, loadShareLogo, type ShareLogo } from '@/lib/share/assets';
import { drawEventCard, type DrawBackground } from '@/lib/share/drawCard';
import type { EventRow } from '@/types/database';

export const SHARE_IMAGE_SIZE = 1080;

export interface RenderedShareImage {
  blob: Blob;
  dataUrl: string;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob a échoué'));
    }, 'image/png');
  });
}

async function resolveDrawBackground(event: EventRow, backgroundIndex: number): Promise<DrawBackground> {
  const choice = resolveBackground(backgroundIndex, Boolean(event.image_url));
  if (choice.kind === 'photo' && event.image_url) {
    try {
      const image = await loadShareBackgroundImage(event.image_url);
      return { kind: 'photo', image, iw: image.naturalWidth || image.width, ih: image.naturalHeight || image.height };
    } catch {
      return { kind: 'gradient', gradient: SHARE_GRADIENTS[0] };
    }
  }
  return { kind: 'gradient', gradient: choice.kind === 'gradient' ? choice.gradient : SHARE_GRADIENTS[0] };
}

/** Génère l'image partagée pour un événement et un fond donnés. */
export async function renderEventShareImage(
  event: EventRow,
  backgroundIndex: number,
  size: number = SHARE_IMAGE_SIZE,
): Promise<RenderedShareImage> {
  const data = toShareCardData(event);
  const accent = categoryAccent(event.kind);

  const [logo, , background] = await Promise.all([
    loadShareLogo().catch(() => null as ShareLogo | null),
    ensureShareFontsLoaded(),
    resolveDrawBackground(event, backgroundIndex),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('contexte canvas 2d indisponible');

  drawEventCard(ctx, { data, background, accent, logo }, size);

  const blob = await canvasToBlob(canvas);
  return { blob, dataUrl: canvas.toDataURL('image/png') };
}
